import { useBusinessOptions } from '@/services/businessOptions'
import { offerCustomerCopy } from '@/services/appCopy'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { Core } from '@form-validation/core'
import { AdminPageHero } from '@/components/common/AdminPageHero'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { adminRoles } from '@/helpers/roles'
import { useAuth } from '@/context/AuthContext'
import { createFormValidation, isFormValid } from '@/helpers/formValidationHelper'
import type { CrmUser, NamedItem } from '@/models/api'
import { loadAssignableUsers } from '@/services/actionService'
import { readError } from '@/services/apiClient'
import { loadCustomer, removeCustomerImage, saveCustomer, uploadCustomerImage, type CustomerDetail } from '@/services/customerService'
import { loadReferenceData } from '@/services/referenceService'

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

interface CustomerFormState {
  name: string
  accountNumber: string
  categoryId: string
  address1: string
  address2: string
  address3: string
  city: string
  county: string
  postcode: string
  contactNumber: string
  emailAddress: string
  accountOwnerId: string
  notes: string
  onStop: boolean
  customerTypeId: string
  statusId: string
  defaultDiscountId: string
  gridRefId: string
  callDay: string
  deliveryDay: string
  preferredContactMethod: string
  url: string
  stopReason: string
}

const blankForm: CustomerFormState = {
  name: '',
  accountNumber: '',
  categoryId: '',
  address1: '',
  address2: '',
  address3: '',
  city: '',
  county: '',
  postcode: '',
  contactNumber: '',
  emailAddress: '',
  accountOwnerId: '',
  notes: '',
  onStop: false,
  customerTypeId: '',
  statusId: '',
  defaultDiscountId: '',
  gridRefId: '',
  callDay: '',
  deliveryDay: '',
  preferredContactMethod: 'CALL',
  url: '',
  stopReason: '',
}

export function LiveCustomerEditor() {
  const { options: businessOptions } = useBusinessOptions()
  const { id } = useParams()
  const navigate = useNavigate()
  const { session } = useAuth()
  const canStop = !!session?.role && adminRoles.includes(session.role)
  const formRef = useRef<HTMLFormElement>(null)
  const fvRef = useRef<Core | null>(null)
  const [form, setForm] = useState<CustomerFormState>(blankForm)
  const [savedId, setSavedId] = useState(id ?? '')
  const [originalOnStop, setOriginalOnStop] = useState(false)
  const [users, setUsers] = useState<CrmUser[]>([])
  const [types, setTypes] = useState<NamedItem[]>([])
  const [statuses, setStatuses] = useState<NamedItem[]>([])
  const [categories, setCategories] = useState<NamedItem[]>([])
  const [discounts, setDiscounts] = useState<NamedItem[]>([])
  const [grids, setGrids] = useState<NamedItem[]>([])
  const [contactMethods, setContactMethods] = useState<NamedItem[]>([])
  const [loading, setLoading] = useState(true)
  const [missing, setMissing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageBusy, setImageBusy] = useState(false)
  const [imageDragOver, setImageDragOver] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const stopChanged = form.onStop !== originalOnStop

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setMissing(false)
    setError(null)
    Promise.all([
      loadAssignableUsers(),
      loadReferenceData('customer-types'),
      loadReferenceData('customer-status'),
      loadReferenceData('customer-categories'),
      loadReferenceData('discounts'),
      loadReferenceData('grid-refs'),
      loadReferenceData('contact-methods'),
      id ? loadCustomer(id) : Promise.resolve(null),
    ])
      .then(([nextUsers, nextTypes, nextStatuses, nextCategories, nextDiscounts, nextGrids, nextMethods, customer]) => {
        if (cancelled) return
        setUsers(nextUsers)
        setTypes(enabledOrCurrent(nextTypes, customer?.customerTypeId, customer?.customerType))
        setStatuses(enabledOrCurrent(nextStatuses, customer?.statusId, customer?.status))
        setCategories(enabledOrCurrent(nextCategories, customer?.categoryId, customer?.category))
        setDiscounts(enabledOrCurrent(nextDiscounts, customer?.defaultDiscountId, customer?.discount))
        setGrids(enabledOrCurrent(nextGrids, customer?.gridRefId, customer?.gridRef))
        setContactMethods(nextMethods.filter((item) => item.enabled))
        if (customer) {
          setForm(formFromCustomer(customer))
          setOriginalOnStop(customer.onStop)
          setSavedId(customer.id)
          setImageUrl(customer.imageUrl)
        } else if (!id) {
          setForm((current) => ({
            ...current,
            accountOwnerId: nextUsers.some((user) => user.crmAccountUserId === session?.businessUserId) ? session?.businessUserId ?? '' : '',
            customerTypeId: firstId(nextTypes),
            statusId: firstId(nextStatuses),
            defaultDiscountId: firstId(nextDiscounts),
            gridRefId: firstId(nextGrids),
          }))
        }
      })
      .catch((caught) => {
        if (cancelled) return
        const message = readError(caught)
        if (id && message.toLowerCase().includes('not found')) setMissing(true)
        else setError(message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [id, session?.businessUserId])

  useEffect(() => {
    const element = formRef.current
    if (!element || loading || missing) return
    const fields: Parameters<typeof createFormValidation>[1]['fields'] = {
      name: { validators: { notEmpty: { message: 'Enter a customer name' } } },
      postcode: { validators: { notEmpty: { message: 'Enter a postcode' } } },
      emailAddress: { validators: { emailAddress: { message: 'Enter a valid email address' } } },
      accountOwnerId: { validators: { notEmpty: { message: 'Choose an account owner' } } },
      customerTypeId: { validators: { notEmpty: { message: 'Choose a customer type' } } },
      statusId: { validators: { notEmpty: { message: 'Choose a customer status' } } },
      defaultDiscountId: { validators: { notEmpty: { message: 'Choose a default discount' } } },
      ...(businessOptions.useGridReferences ? { gridRefId: { validators: { notEmpty: { message: 'Choose a grid reference' } } } } : {}),
    }
    if (stopChanged) {
      fields.stopReason = { validators: { notEmpty: { message: 'Enter a reason' } } }
    }
    const fv = createFormValidation(element, { fields })
    fvRef.current = fv
    return () => {
      fv.destroy()
      fvRef.current = null
    }
  }, [loading, missing, stopChanged, businessOptions.useGridReferences])

  function patch(next: Partial<CustomerFormState>) {
    setForm((current) => ({ ...current, ...next }))
  }

  function acceptImage(file: File | undefined) {
    if (!file || !id) return
    const allowed = /\.(jpe?g|png|gif|bmp|tiff?|svg|webp)$/i
    if (!file.type.startsWith('image/') && !allowed.test(file.name)) {
      setError('Choose a jpg, png, gif, bmp, tiff, svg, or webp image.')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be 10 MB or smaller.')
      return
    }
    void changeImage(file)
  }

  async function changeImage(file: File) {
    if (!id) return
    setImageBusy(true)
    setError(null)
    setConfirmRemove(false)
    setImageDragOver(false)
    try {
      setImageUrl(await uploadCustomerImage(id, file))
    } catch (caught) {
      setError(readError(caught))
    } finally {
      setImageBusy(false)
    }
  }

  async function removeImage() {
    if (!id) return
    setImageBusy(true)
    setError(null)
    try {
      await removeCustomerImage(id)
      setImageUrl(null)
      setConfirmRemove(false)
    } catch (caught) {
      setError(readError(caught))
    } finally {
      setImageBusy(false)
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!(await isFormValid(fvRef.current))) return
    setBusy(true)
    setError(null)
    try {
      const customerId = await saveCustomer({
        id: savedId || null,
        name: form.name.trim(),
        accountNumber: emptyToNull(form.accountNumber),
        address1: emptyToNull(form.address1),
        address2: emptyToNull(form.address2),
        address3: emptyToNull(form.address3),
        city: emptyToNull(form.city),
        county: emptyToNull(form.county),
        postcode: emptyToNull(form.postcode),
        callDay: emptyToNull(form.callDay),
        deliveryDay: emptyToNull(form.deliveryDay),
        contactNumber: emptyToNull(form.contactNumber),
        emailAddress: emptyToNull(form.emailAddress),
        notes: emptyToNull(form.notes),
        url: emptyToNull(form.url),
        preferredContactMethod: emptyToNull(form.preferredContactMethod),
        accountOwnerId: form.accountOwnerId,
        customerTypeId: Number(form.customerTypeId),
        statusId: Number(form.statusId),
        gridRefId: Number(form.gridRefId),
        defaultDiscountId: Number(form.defaultDiscountId),
        categoryId: form.categoryId ? Number(form.categoryId) : null,
        ...(stopChanged && canStop ? { onStop: form.onStop, stopReason: form.stopReason.trim() } : {}),
      })
      setSavedId(customerId)
      offerCustomerCopy(customerId)
      navigate(`/customers/${customerId}`)
    } catch (caught) {
      setError(readError(caught))
      setBusy(false)
    }
  }

  if (missing) {
    return <Alert variant="warning">Customer not found. <Link to="/customers">Back to customers</Link></Alert>
  }

  const owners = users.some((user) => user.crmAccountUserId === form.accountOwnerId) || !form.accountOwnerId
    ? users
    : [...users, { userId: form.accountOwnerId, crmAccountUserId: form.accountOwnerId, emailAddress: null, fullName: 'Current owner', role: '', enabled: true }]
  const dayOptions = (current: string) => current && !weekdays.includes(current) ? [current, ...weekdays] : weekdays
  const contactOptions = Array.from(new Set(['CALL', ...contactMethods.map((item) => item.name), form.preferredContactMethod].filter(Boolean)))

  return (
    <>
      <AdminPageHero
        eyebrow="Workspace / CRM"
        title={id ? 'Edit customer' : 'New customer'}
        summary="Account, contact and order preferences."
      />
      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? <LoadingSpinner text="Loading customer" size="3x" /> : (
        <Form ref={formRef} noValidate onSubmit={(event) => void submit(event)}>
          <Row className="g-4">
            <Col lg={7}>
              <Card>
                <Card.Header>Customer & contact details</Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <TextField label="Customer name" name="name" value={form.name} onChange={(name) => patch({ name })} />
                      <TextField label="Account number" name="accountNumber" value={form.accountNumber} onChange={(accountNumber) => patch({ accountNumber })} />
                      <SelectField label="Category" name="categoryId" value={form.categoryId} placeholder="Not set" onChange={(categoryId) => patch({ categoryId })} options={categories.map((item) => ({ value: String(item.id), label: item.name }))} />
                      <TextField label="Address" name="address1" value={form.address1} onChange={(address1) => patch({ address1 })} />
                      <TextField label="Address line 2" name="address2" value={form.address2} onChange={(address2) => patch({ address2 })} />
                      <TextField label="Address line 3" name="address3" value={form.address3} onChange={(address3) => patch({ address3 })} />
                      <TextField label="City" name="city" value={form.city} onChange={(city) => patch({ city })} />
                      <TextField label="County" name="county" value={form.county} onChange={(county) => patch({ county })} />
                      <TextField label="Postcode" name="postcode" value={form.postcode} onChange={(postcode) => patch({ postcode })} />
                    </Col>
                    <Col md={6}>
                      <TextField label="Telephone" name="contactNumber" value={form.contactNumber} onChange={(contactNumber) => patch({ contactNumber })} />
                      <TextField label="Email" name="emailAddress" type="email" value={form.emailAddress} onChange={(emailAddress) => patch({ emailAddress })} />
                      <TextField label="Website" name="url" value={form.url} onChange={(url) => patch({ url })} />
                      <SelectField
                        label="Account owner"
                        name="accountOwnerId"
                        value={form.accountOwnerId}
                        onChange={(accountOwnerId) => patch({ accountOwnerId })}
                        options={owners.map((user) => ({ value: user.crmAccountUserId, label: user.fullName || user.emailAddress || 'User' }))}
                      />
                      <TextField label="General notes" name="notes" type="textarea" value={form.notes} onChange={(notes) => patch({ notes })} />
                      {id && (
                        <div className="mb-3">
                          <Form.Label>Customer image</Form.Label>
                          {imageUrl ? (
                            <div className="customer-editor-image">
                              <img src={imageUrl} alt="" />
                              {!confirmRemove ? (
                                <Button type="button" variant="outline-danger" size="sm" disabled={imageBusy} onClick={() => setConfirmRemove(true)}>Remove image</Button>
                              ) : (
                                <div className="d-flex gap-2">
                                  <Button type="button" variant="danger" size="sm" disabled={imageBusy} onClick={() => void removeImage()}>Remove image</Button>
                                  <Button type="button" variant="outline-secondary" size="sm" disabled={imageBusy} onClick={() => setConfirmRemove(false)}>Keep image</Button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <button
                              type="button"
                              className={`customer-image-drop${imageDragOver ? ' is-dragover' : ''}`}
                              disabled={imageBusy}
                              onClick={() => imageInputRef.current?.click()}
                              onDragEnter={(event) => {
                                event.preventDefault()
                                setImageDragOver(true)
                              }}
                              onDragOver={(event) => {
                                event.preventDefault()
                                setImageDragOver(true)
                              }}
                              onDragLeave={(event) => {
                                if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
                                setImageDragOver(false)
                              }}
                              onDrop={(event) => {
                                event.preventDefault()
                                setImageDragOver(false)
                                acceptImage(event.dataTransfer.files?.[0])
                              }}
                            >
                              <span>{imageBusy ? 'Updating image' : 'Drop an image here, or click to choose'}</span>
                              <small>Jpg, png, gif, bmp, tiff, svg or webp. 10 MB maximum.</small>
                            </button>
                          )}
                          <input
                            ref={imageInputRef}
                            type="file"
                            hidden
                            accept=".jpg,.jpeg,.png,.gif,.bmp,.tif,.tiff,.svg,.webp,image/*"
                            onChange={(event) => {
                              acceptImage(event.target.files?.[0])
                              event.target.value = ''
                            }}
                          />
                        </div>
                      )}
                      <Form.Check
                        id="customer-on-stop"
                        label="Account on stop"
                        disabled={!canStop}
                        checked={form.onStop}
                        onChange={(event) => patch({ onStop: event.target.checked, stopReason: event.target.checked === originalOnStop ? '' : form.stopReason })}
                      />
                      {stopChanged && (
                        <div className="mt-3">
                          <TextField
                            label={form.onStop ? 'Why is this customer going on stop?' : 'Why is the stop being removed?'}
                            name="stopReason"
                            type="textarea"
                            value={form.stopReason}
                            onChange={(stopReason) => patch({ stopReason })}
                          />
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={5}>
              <Card>
                <Card.Header>Order preferences</Card.Header>
                <Card.Body>
                  <SelectField label="Customer type" name="customerTypeId" value={form.customerTypeId} onChange={(customerTypeId) => patch({ customerTypeId })} options={types.map((item) => ({ value: String(item.id), label: item.name }))} />
                  <SelectField label="Customer status" name="statusId" value={form.statusId} onChange={(statusId) => patch({ statusId })} options={statuses.map((item) => ({ value: String(item.id), label: item.name }))} />
                  <SelectField label="Default discount" name="defaultDiscountId" value={form.defaultDiscountId} onChange={(defaultDiscountId) => patch({ defaultDiscountId })} options={discounts.map((item) => ({ value: String(item.id), label: item.name }))} />
                  {businessOptions.useGridReferences && <SelectField label="Grid reference" name="gridRefId" value={form.gridRefId} onChange={(gridRefId) => patch({ gridRefId })} options={grids.map((item) => ({ value: String(item.id), label: item.name }))} />}
                  {businessOptions.useCallDay && <SelectField label="Call day" name="callDay" value={form.callDay} placeholder="Not set" onChange={(callDay) => patch({ callDay })} options={dayOptions(form.callDay).map((day) => ({ value: day, label: day }))} />}
                  {businessOptions.useDeliveryDay && <SelectField label="Delivery day" name="deliveryDay" value={form.deliveryDay} placeholder="Not set" onChange={(deliveryDay) => patch({ deliveryDay })} options={dayOptions(form.deliveryDay).map((day) => ({ value: day, label: day }))} />}
                  <SelectField label="Preferred contact" name="preferredContactMethod" value={form.preferredContactMethod} onChange={(preferredContactMethod) => patch({ preferredContactMethod })} options={contactOptions.map((method) => ({ value: method, label: method }))} />
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <div className="mt-4 d-flex gap-2">
            <Button type="submit" disabled={busy}>Save customer</Button>
            <Link className="btn btn-outline-secondary" to={savedId ? `/customers/${savedId}` : '/customers'}>Cancel</Link>
          </div>
        </Form>
      )}
    </>
  )
}

function TextField({
  label,
  name,
  value,
  onChange,
  type = 'text',
}: {
  label: string
  name: string
  value: string
  onChange: (value: string) => void
  type?: string
}) {
  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        name={name}
        value={value}
        as={type === 'textarea' ? 'textarea' : 'input'}
        type={type === 'textarea' ? undefined : type}
        onChange={(event) => onChange(event.target.value)}
      />
    </Form.Group>
  )
}

function SelectField({
  label,
  name,
  value,
  onChange,
  options,
  placeholder = 'Choose',
}: {
  label: string
  name: string
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
  placeholder?: string
}) {
  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Select name={name} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{placeholder}</option>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Form.Select>
    </Form.Group>
  )
}

function formFromCustomer(customer: CustomerDetail): CustomerFormState {
  return {
    name: customer.name ?? '',
    accountNumber: customer.accountNumber ?? '',
    categoryId: customer.categoryId ? String(customer.categoryId) : '',
    address1: customer.address1 ?? '',
    address2: customer.address2 ?? '',
    address3: customer.address3 ?? '',
    city: customer.city ?? '',
    county: customer.county ?? '',
    postcode: customer.postcode ?? '',
    contactNumber: customer.contactNumber ?? '',
    emailAddress: customer.emailAddress ?? '',
    accountOwnerId: customer.accountOwnerId ?? '',
    notes: customer.notes ?? '',
    onStop: customer.onStop,
    customerTypeId: customer.customerTypeId ? String(customer.customerTypeId) : '',
    statusId: customer.statusId ? String(customer.statusId) : '',
    defaultDiscountId: customer.defaultDiscountId ? String(customer.defaultDiscountId) : '',
    gridRefId: customer.gridRefId ? String(customer.gridRefId) : '',
    callDay: customer.callDay ?? '',
    deliveryDay: customer.deliveryDay ?? '',
    preferredContactMethod: customer.preferredContactMethod || 'CALL',
    url: customer.url ?? '',
    stopReason: '',
  }
}

function enabledOrCurrent(items: NamedItem[], id?: number | null, name?: string | null): NamedItem[] {
  const enabled = items.filter((item) => item.enabled)
  if (!id || enabled.some((item) => item.id === id)) return enabled
  return [{ id, name: name || 'Current', enabled: true, order: 0 }, ...enabled]
}

function firstId(items: NamedItem[]): string {
  const enabled = items.find((item) => item.enabled)
  return enabled ? String(enabled.id) : ''
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}
